/**
 * Header Component
 * Premium navigation with sophisticated mega menu featuring products
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useCartStore, selectCartQuantity } from '~/store/cart-store';
import { SearchModal } from '../search/SearchModal';
import { useBrandCollections } from '~/hooks/useBrandCollections';
import {
  IconHeadset,
  IconSearch,
  IconUser,
  IconCart,
  IconMenu,
  IconClose,
  IconChevronDown,
  IconArrowRight,
  IconBluetooth,
  IconUsb,
  IconWireless,
  IconNoiseCancelling,
  IconMicrosoft,
  IconHome,
  IconOffice,
  IconShippingFast,
  IconReturn,
} from '../ui/Icons';

// Static navigation sections (By Use Case and By Connection)
// These link to /products until dedicated collections are created in Shopify
const staticSections = [
  {
    title: 'By Use Case',
    links: [
      { label: 'Microsoft Teams', href: '/products?use=teams', icon: IconMicrosoft },
      { label: 'Zoom Certified', href: '/products?use=zoom' },
      { label: 'Call Centre', href: '/products?use=call-centre', icon: IconOffice },
      { label: 'Work From Home', href: '/products?use=wfh', icon: IconHome },
      { label: 'Noise Cancelling', href: '/products?use=anc', icon: IconNoiseCancelling },
    ],
  },
  {
    title: 'By Connection',
    links: [
      { label: 'Bluetooth Wireless', href: '/products?connection=bluetooth', icon: IconBluetooth },
      { label: 'USB Wired', href: '/products?connection=usb', icon: IconUsb },
      { label: 'DECT Wireless', href: '/products?connection=dect', icon: IconWireless },
      { label: 'Dual Connectivity', href: '/products?connection=dual' },
    ],
  },
];

// Navigation structure
const navigation = {
  shopNew: {
    label: 'Shop New',
    featured: {
      title: 'Quick Links',
      collections: [
        {
          name: 'All Headsets',
          description: 'Browse our full range',
          tag: 'Shop',
          href: '/products',
          icon: IconHeadset,
        },
        {
          name: 'Refurbished',
          description: 'Quality tested, great value',
          tag: 'Save 40%',
          href: '/collections/refurbished',
          icon: IconArrowRight,
        },
      ],
      promo: {
        title: 'Free Next-Day Delivery',
        description: 'On orders over £75',
        href: '/shipping',
      },
    },
  },
  links: [
    { label: 'Refurbished', href: '/collections/refurbished', badge: 'Save 40%' },
    { label: 'Support', href: '/support' },
    { label: 'About', href: '/about' },
  ],
};

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const cartQuantity = useCartStore(selectCartQuantity);
  const openCart = useCartStore((state) => state.openCart);

  // Fetch dynamic brand collections
  const { brands } = useBrandCollections();

  // Build navigation sections with dynamic brands
  const shopNewSections = [
    {
      title: 'By Brand',
      links: brands,
    },
    ...staticSections,
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setMegaMenuOpen(true);
  };

  const handleMenuLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(false);
    }, 150);
  };

  return (
    <>
      {/* Announcement Bar */}
      <div className="w-full bg-gradient-to-r from-brand-orange via-brand-orange-light to-brand-orange text-white text-center py-2.5 px-4 text-sm font-medium">
        <div className="flex items-center justify-center gap-4">
          <span className="hidden sm:flex items-center gap-2">
            <IconShippingFast size={16} />
            Free Next-Day Delivery on orders over £75
          </span>
          <span className="hidden sm:inline text-white/40">|</span>
          <span className="flex items-center gap-2">
            <IconReturn size={16} />
            30-Day Returns
          </span>
        </div>
      </div>

      <header
        className={`sticky top-0 z-40 w-full transition-all duration-300 ${
          isScrolled
            ? 'bg-surface-black/95 backdrop-blur-xl border-b border-surface-slate/50 shadow-2xl'
            : 'bg-surface-black border-b border-surface-slate/30'
        }`}
      >
        <div className="container-wide">
          <nav className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 group">
              <img
                src="https://cdn.shopify.com/s/files/1/0952/3678/4460/files/itr-logo.png?v=1770583066"
                alt="IT Remarketing"
                className="h-14 sm:h-[66px] w-auto brightness-0 invert transition-transform duration-300 group-hover:scale-105"
              />
              <span className="hidden sm:block text-sm font-mono uppercase tracking-[0.2em] text-accent-silver/60 border-l border-surface-slate pl-3">
                Secure. Simple.<br />Sustainable.
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1" ref={megaMenuRef}>
              {/* Shop New - Mega Menu Trigger */}
              <div
                className="relative"
                onMouseEnter={handleMenuEnter}
                onMouseLeave={handleMenuLeave}
              >
                <button
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 flex items-center gap-1.5 rounded-lg ${
                    megaMenuOpen
                      ? 'text-brand-orange bg-brand-orange/10'
                      : 'text-accent-silver hover:text-accent-white hover:bg-surface-slate/50'
                  }`}
                >
                  {navigation.shopNew.label}
                  <IconChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${megaMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Mega Menu */}
                <AnimatePresence>
                  {megaMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[900px] bg-surface-coal/95 backdrop-blur-xl rounded-2xl border border-surface-slate/50 shadow-2xl overflow-hidden"
                      onMouseEnter={handleMenuEnter}
                      onMouseLeave={handleMenuLeave}
                    >
                      <div className="grid grid-cols-12 gap-0 divide-x divide-surface-slate/30">
                        {/* Category Columns */}
                        <div className="col-span-8 grid grid-cols-3 gap-0 divide-x divide-surface-slate/30">
                          {shopNewSections.map((section) => (
                            <div key={section.title} className="p-6">
                              <h3 className="text-[11px] font-mono uppercase tracking-[0.15em] text-accent-silver/70 mb-4">
                                {section.title}
                              </h3>
                              <ul className="space-y-1">
                                {section.links.map((link) => (
                                  <li key={link.href}>
                                    <a
                                      href={link.href}
                                      className={`flex items-center gap-2 py-2 px-2 -mx-2 rounded-lg text-sm transition-all duration-150 group/link ${
                                        link.isViewAll
                                          ? 'text-brand-orange hover:bg-brand-orange/10'
                                          : 'text-accent-platinum hover:text-accent-white hover:bg-surface-slate/50'
                                      }`}
                                      onClick={() => setMegaMenuOpen(false)}
                                    >
                                      {link.icon && (
                                        <link.icon size={16} className="text-accent-silver group-hover/link:text-brand-orange transition-colors" />
                                      )}
                                      <span>{link.label}</span>
                                      {link.badge && (
                                        <span className="ml-auto text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-brand-orange/20 text-brand-orange rounded">
                                          {link.badge}
                                        </span>
                                      )}
                                      {link.isViewAll && (
                                        <IconArrowRight size={14} className="ml-auto transition-transform group-hover/link:translate-x-1" />
                                      )}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>

                        {/* Featured Section */}
                        <div className="col-span-4 p-6 bg-surface-graphite/50">
                          <h3 className="text-[11px] font-mono uppercase tracking-[0.15em] text-accent-silver/70 mb-4">
                            {navigation.shopNew.featured.title}
                          </h3>

                          {/* Featured Collections */}
                          <div className="space-y-3 mb-6">
                            {navigation.shopNew.featured.collections.map((collection) => (
                              <a
                                key={collection.href}
                                href={collection.href}
                                className="flex items-center gap-3 p-3 -mx-3 rounded-xl bg-surface-coal/50 border border-surface-slate/30 hover:border-brand-orange/30 hover:bg-surface-coal transition-all duration-200 group/collection"
                                onClick={() => setMegaMenuOpen(false)}
                              >
                                {/* Collection Icon */}
                                <div className="w-12 h-12 bg-brand-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <collection.icon size={22} className="text-brand-orange" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-[10px] font-mono uppercase tracking-wider text-brand-orange">
                                    {collection.tag}
                                  </span>
                                  <h4 className="text-sm font-medium text-accent-white group-hover/collection:text-brand-orange transition-colors">
                                    {collection.name}
                                  </h4>
                                  <p className="text-xs text-accent-silver truncate">
                                    {collection.description}
                                  </p>
                                </div>
                                <IconArrowRight size={16} className="text-accent-silver group-hover/collection:text-brand-orange group-hover/collection:translate-x-1 transition-all" />
                              </a>
                            ))}
                          </div>

                          {/* Promo Banner */}
                          <a
                            href={navigation.shopNew.featured.promo.href}
                            className="block p-4 rounded-xl bg-gradient-to-br from-brand-orange/20 to-brand-orange/5 border border-brand-orange/20 hover:border-brand-orange/40 transition-colors"
                            onClick={() => setMegaMenuOpen(false)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-brand-orange/20 rounded-lg flex items-center justify-center">
                                <IconShippingFast size={20} className="text-brand-orange" />
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-accent-white">
                                  {navigation.shopNew.featured.promo.title}
                                </h4>
                                <p className="text-xs text-accent-silver">
                                  {navigation.shopNew.featured.promo.description}
                                </p>
                              </div>
                            </div>
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Other nav links */}
              {navigation.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="relative px-4 py-2 text-sm font-medium text-accent-silver hover:text-accent-white hover:bg-surface-slate/50 rounded-lg transition-all duration-200 flex items-center gap-2"
                >
                  {link.label}
                  {link.badge && (
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">
                      {link.badge}
                    </span>
                  )}
                </a>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1">
              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="w-10 h-10 flex items-center justify-center text-accent-silver hover:text-accent-white hover:bg-surface-slate/50 rounded-xl transition-all duration-200"
                aria-label="Search"
              >
                <IconSearch size={20} />
              </button>

              {/* Account */}
              <a
                href="/account"
                className="hidden sm:flex w-10 h-10 items-center justify-center text-accent-silver hover:text-accent-white hover:bg-surface-slate/50 rounded-xl transition-all duration-200"
                aria-label="Account"
              >
                <IconUser size={20} />
              </a>

              {/* Cart */}
              <button
                onClick={openCart}
                className="relative w-10 h-10 flex items-center justify-center text-accent-silver hover:text-accent-white hover:bg-surface-slate/50 rounded-xl transition-all duration-200"
                aria-label="Cart"
              >
                <IconCart size={20} />
                {cartQuantity > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-brand-orange text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg shadow-brand-orange/30"
                  >
                    {cartQuantity > 9 ? '9+' : cartQuantity}
                  </motion.span>
                )}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-accent-silver hover:text-accent-white hover:bg-surface-slate/50 rounded-xl transition-all duration-200"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <IconClose size={20} /> : <IconMenu size={20} />}
              </button>
            </div>
          </nav>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="lg:hidden border-t border-surface-slate/30 overflow-hidden bg-surface-coal/95 backdrop-blur-xl"
            >
              <div className="container-wide py-6 space-y-6">
                {/* Shop Categories */}
                {shopNewSections.map((section) => (
                  <div key={section.title}>
                    <h3 className="text-[11px] font-mono uppercase tracking-[0.15em] text-accent-silver/70 mb-3">
                      {section.title}
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {section.links.slice(0, 4).map((link) => (
                        <a
                          key={link.href}
                          href={link.href}
                          className="flex items-center gap-2 px-4 py-3 bg-surface-graphite/50 rounded-xl text-sm text-accent-platinum hover:bg-surface-slate/50 hover:text-accent-white transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {link.icon && <link.icon size={16} className="text-accent-silver" />}
                          {link.label}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Other Links */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-surface-slate/30">
                  {navigation.links.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="px-4 py-2 text-sm text-accent-silver hover:text-accent-white transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Search Modal */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
